import {
  getSessionRegionOption,
  getSessionStorageFilters,
} from '@src/apps/CallSummary/CallsFilter/sessionStorageUtils';
import {
  checkIsInternal,
  getTinGroupId,
  isHighVolumeTinFlow,
} from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import { ENDPOINTS } from './apiUrlConstants';
import { pollBulkJobParallel } from '@src/utility/CommonBulkPoll';

const _httpService = new HttpService();

const isContractBulkFlow = () =>
  sessionStorage.getItem('hiVolumeContractFlow') === 'true';

const getCallsPayload = (payload: any) => {
  const tinGroupId = getTinGroupId() || payload?.TinGroupId || sessionStorage.getItem('tinGroupId');
  const payloadWithoutTinGroupId = { ...(payload || {}) };
  delete payloadWithoutTinGroupId.TinGroupId;

  if (isContractBulkFlow()) {
    return payloadWithoutTinGroupId;
  }

  if (Array.isArray(payload?.ContractIds) && payload.ContractIds.length > 0) {
    return payloadWithoutTinGroupId;
  }

  if (!isHighVolumeTinFlow()) {
    return payload;
  }

  return {
    ...payloadWithoutTinGroupId,
    ...(tinGroupId ? { TinGroupId: tinGroupId } : {}),
  };
};

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL'));
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL'));
}

export const getSummaryData = async (payload: any, geoCriteria?: any) => {
  // Geo Flow uses the Core API directly; high-volume polling remains below.
  const isPollingFlow = geoCriteria !== null && geoCriteria !== undefined && !isContractBulkFlow();
  if (isPollingFlow) {
    try {
      const responseData: any = await _httpService.push(ENDPOINTS.CALLS_SUMMARY, getCallsPayload(payload));
      return responseData;
    } catch (error) {
      console.error('Get Geo Call Summary Error:', error);
      return null;
    }
  } else {
    // ============================================================
    // NORMAL FLOW - Use Standard API (no disturbance)
    // URL: API_DOMAIN_INTERNAL or API_DOMAIN_EXTERNAL
    // Endpoint: /calls/callsSummary (immediate response)
    // ============================================================


    // CHANGE FOR HIGH VOLUME TIN FLOW - Use Bulk API for all summary data requests to avoid timeouts, even without geo-segmentation

    const isHighVolumeTin = isHighVolumeTinFlow() || isContractBulkFlow();
    const url = 'calls-job/summary';

    if (isHighVolumeTin) {
      try {
        const pollingResult = await pollBulkJobParallel<any>(url, getCallsPayload(payload));
        return { StatusCode: 200, Data: pollingResult };
      } catch (error) {
        console.error(`Error occurred while polling for ${url}:`, error);
        return null;
      }
    } else {
      const url = ENDPOINTS.CALLS_SUMMARY;
      try {
        const responseData: any = await _httpService.push(url, getCallsPayload(payload));
        return responseData;
      } catch {
        return null;
      }
    }
  }
};

export const getQuickFiltersData = (payload) => {
  const URL =
    payload.ProvTinListFilter && 'ProviderSysKey' in payload.ProvTinListFilter
      ? ENDPOINTS.QUICK_FILTER
      : ENDPOINTS.QUICK_FILTER_EXTENDED_TINS;
  const fetchData = async () => {
    if (
      URL === ENDPOINTS.QUICK_FILTER_EXTENDED_TINS &&
      sessionStorage.getItem('contractIdNavigation') === 'true'
    ) {
      return null;
    }

    try {
      const response: any = await _httpService.push(URL, payload);
      return response;
    } catch {
      return null;
    }
  };
  return fetchData();
};

export const getCallTypeData = async (payload: any, geoCriteria?: any) => {
  // Geo Flow uses the Core API directly; high-volume polling remains below.
  const isPollingFlow = geoCriteria !== null && geoCriteria !== undefined && !isContractBulkFlow();

  if (isPollingFlow) {
    try {
      const responseData: any = await _httpService.push(ENDPOINTS.CALLS_TYPE, getCallsPayload(payload));
      return responseData;
    } catch (error) {
      console.error('Get Geo Call Type Error:', error);
      return null;
    }
  } else if (isHighVolumeTinFlow() || isContractBulkFlow()) {
    const url = ENDPOINTS.CALLS_TYPE_JOB;
      try {
        const pollingResult = await pollBulkJobParallel<any>(url, getCallsPayload(payload));
        return { StatusCode: 200, Data: pollingResult };
      } catch (error) {
        console.error(`Error occurred while polling for ${url}:`, error);
        return null;
      }
  } else {
    // ============================================================
    // NORMAL FLOW - Use Standard API (no disturbance)
    // URL: API_DOMAIN_INTERNAL or API_DOMAIN_EXTERNAL
    // Endpoint: /calls/callType (immediate response)
    // ============================================================
    const url = ENDPOINTS.CALLS_TYPE;
    try {
      const responseData: any = await _httpService.push(url, getCallsPayload(payload));
      return responseData;
    } catch {
      return null;
    }
  }
};

export const getCallMonthData = async (payload: any, geoCriteria?: any) => {
  // Geo Flow uses the Core API directly; high-volume polling remains below.
  const isPollingFlow = geoCriteria !== null && geoCriteria !== undefined && !isContractBulkFlow();

  if (isPollingFlow) {
    try {
      const responseData: any = await _httpService.push(ENDPOINTS.CALLS_MONTH, getCallsPayload(payload));
      return responseData;
    } catch (error) {
      console.error('Get Geo Call Month Error:', error);
      return null;
    }
  } else if (isHighVolumeTinFlow() || isContractBulkFlow()) {
    const url = ENDPOINTS.CALLS_MONTH_JOB;
      try {
        const pollingResult = await pollBulkJobParallel<any>(url, getCallsPayload(payload));
        return { StatusCode: 200, Data: pollingResult };
      } catch (error) {
        console.error(`Error occurred while polling for ${url}:`, error);
        return null;
      }
  }
  else {
    // ============================================================
    // NORMAL FLOW - Use Standard API (no disturbance)
    // URL: API_DOMAIN_INTERNAL or API_DOMAIN_EXTERNAL
    // Endpoint: /calls/callByMonth (immediate response)
    // ============================================================
    const url = ENDPOINTS.CALLS_MONTH;
    try {
      const responseData: any = await _httpService.push(url, getCallsPayload(payload));
      return responseData;
    } catch {
      return null;
    }
  }
};

export const getTaxIdList = async (payload: any) => {
  if (sessionStorage.getItem('contractIdNavigation') === 'true') {
    return null;
  }

  // Determine which endpoint to use based on payload structure
  // ProviderSysKey → standard-search (returns TINNAME, Tinname, Tin)
  // Tin (comma-separated) → extended-tins (returns TinName, Tin)
  const url =
    payload.ProvTinListFilter && 'ProviderSysKey' in payload.ProvTinListFilter
      ? ENDPOINTS.QUICK_FILTER
      : ENDPOINTS.QUICK_FILTER_EXTENDED_TINS;

  try {
    const responseData: any = await _httpService.push(url, payload);
    return responseData;
  } catch (error) {
    console.error('Get Tax ID List Error:', error);
    return null;
  }
};

export const getGeoTaxIdList = async (params: {
  timePeriod: string;
  region: string;
  state?: string;
  market?: string[];
  timeFilterText?: string;
  lob?: string;
}): Promise<any[]> => {
  const callsFilter: Record<string, any> = {
    TimeFilter: params.timePeriod,
    Region: params.region,
  };
  if (params.timeFilterText) callsFilter.TimeFilterText = params.timeFilterText;
  if (params.state) callsFilter.State = params.state;
  if (params.market?.length) callsFilter.Market = params.market;
  if (params.lob && params.lob.toUpperCase() !== 'ALL') callsFilter.Lob = params.lob;
  const payload = {
    RequestedJourney: 'callsGeoTinListFilter',
    CallsFilter: callsFilter,
  };

  try {
    const responseData: any = await _httpService.push(ENDPOINTS.QUICK_FILTER, payload);
    return responseData?.Data || [];
  } catch (error) {
    console.error('[Geo TaxId Filter] API error:', error);
    return [];
  }
};

export const getRegionData = async () => {
  const filters = getSessionStorageFilters();
  const PriorAuthAffordabilityFilter = {
    Region: getSessionRegionOption()?.value || 'NATIONAL',
    State: '',
    TimeFilter: filters.timePeriod || 'Last12Months',
  };
  const payload = {
    RequestedJourney: 'npsDriverFilter',
    PriorAuthAffordabilityFilter,
  };

  try {
    const response: any = await _httpService.push(
      ENDPOINTS.QUICK_FILTER,
      payload
    );
    if (response?.Data) {
      return response.Data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching geo region data:', error);
    return null;
  }
};

export const getStateData = async (region: string) => {
  const filters = getSessionStorageFilters();
  const PriorAuthAffordabilityFilter = {
    Region: region || '',
    State: '',
    TimeFilter: filters.timePeriod || 'Last12Months',
  };
  const payload = {
    RequestedJourney: 'npsDriverFilter',
    PriorAuthAffordabilityFilter,
  };

  try {
    const response: any = await _httpService.push(
      ENDPOINTS.QUICK_FILTER,
      payload
    );
    if (response?.Data?.StateList) {
      return response.Data.StateList;
    }
    return [];
  } catch (error) {
    console.error('Error fetching state data:', error);
    return [];
  }
};

export const getMarketData = async (region: string, state?: string) => {
  const filters = getSessionStorageFilters();

  const PriorAuthFilter: any = {
    TimeFilter: filters.timePeriod || 'Last12Months',
    ItemsBy: 'MARKETNAME',
    Region: region || '',
  };

  if (state) {
    PriorAuthFilter.State = state;
  }

  const payload = {
    RequestedJourney: 'priorAuthFilter',
    PriorAuthFilter,
  };

  try {
    const response: any = await _httpService.push(
      ENDPOINTS.MARKET_FILTER,
      payload
    );
    if (response?.Data) {
      return response.Data;
    }
    return [];
  } catch (error) {
    console.error('Error fetching market data:', error);
    return [];
  }
};

export const getCallsGeoTinCountData = async (payload: any) => {
  try {
    const response: any = await _httpService.push(
      ENDPOINTS.CALLS_GEO_TIN_COUNT,
      payload
    );
    if (response?.Data) {
      return response.Data;
    }
    return {};
  } catch (error) {
    console.error('Error fetching calls geo tin count data:', error);
    return {};
  }
};